import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axiosClient from "../../api/axiosClient";
import { CheckCircle2, ChevronDown, ChevronUp, Package, User, MapPin } from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────────────
   GLASS TILE  – exact copy from AdminProduct.jsx
───────────────────────────────────────────────────────────────────────────── */
function GlassTile({ children, className = "" }) {
  return (
    <div
      className={`bg-white/40 backdrop-blur-xl border border-white/25 rounded-2xl shadow-lg ${className}`}
      style={{ WebkitBackdropFilter: "blur(14px) saturate(140%)" }}
    >
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   API HELPERS
───────────────────────────────────────────────────────────────────────────── */
const fetchPendingOrders = () =>
  axiosClient.get("/admin/orders/pending").then((r) => r.data);

const confirmOrderReq = (orderId) =>
  axiosClient.patch(`/admin/${orderId}/confirm`);

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
function fmt(amount) {
  return Number(amount ?? 0).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/* ─────────────────────────────────────────────────────────────────────────────
   ORDER STATUS BADGE
───────────────────────────────────────────────────────────────────────────── */
function StatusBadge({ status }) {
  const map = {
    CONFIRMED:  "bg-green-100/80  text-green-700  border border-green-200",
    PROCESSING: "bg-blue-100/80   text-blue-700   border border-blue-200",
    PENDING:    "bg-amber-100/80  text-amber-700  border border-amber-200",
    CANCELLED:  "bg-red-100/80    text-red-700    border border-red-200",
  };
  const cls = map[status] ?? "bg-white/60 text-gray-600 border border-white/30";
  return (
    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${cls}`}>
      {status ?? "—"}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   SINGLE ORDER CARD
───────────────────────────────────────────────────────────────────────────── */
function OrderCard({ order, onConfirm, isConfirming }) {
  const [expanded, setExpanded] = useState(false);
  const isConfirmed = order.orderStatus === "CONFIRMED";

  const addressParts = [
    order.line1,
    order.line2,
    order.city,
    order.state,
    order.postalCode,
  ].filter(Boolean);

  return (
    <GlassTile className="p-6">

      {/* ── ORDER HEADER ── */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">
            Order #{order.orderId}
          </h2>
          <p className="text-sm text-gray-600 mt-0.5">
            {fmtDate(order.createdAt)}
          </p>
        </div>

        <div className="flex items-center gap-4">
          <StatusBadge status={order.orderStatus} />

          <span className="font-semibold text-gray-700">
            {fmt(order.totalAmount)}
          </span>

          {isConfirmed ? (
            <span className="flex items-center gap-1.5 text-green-600 font-medium text-sm">
              <CheckCircle2 size={16} />
              Confirmed
            </span>
          ) : (
            <button
              disabled={isConfirming}
              onClick={() => onConfirm(order.orderId)}
              className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
            >
              {isConfirming ? "Confirming…" : "CONFIRM"}
            </button>
          )}

          <button
            className="text-blue-600 font-medium text-sm flex items-center gap-1"
            onClick={() => setExpanded((p) => !p)}
          >
            {expanded ? (
              <><ChevronUp size={16} /> Hide Details</>
            ) : (
              <><ChevronDown size={16} /> View Details</>
            )}
          </button>
        </div>
      </div>

      {/* ── EXPANDED DETAILS ── */}
      {expanded && (
        <div className="mt-6 space-y-4">

          {/* Customer + Address row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Customer */}
            <GlassTile className="p-4 bg-white/30">
              <div className="flex items-center gap-2 mb-2">
                <User size={15} className="text-gray-500" />
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                  Customer
                </p>
              </div>
              <p className="font-semibold text-gray-800">{order.userName ?? "—"}</p>
              <p className="text-sm text-gray-600">{order.userEmail ?? "—"}</p>
              <p className="text-xs text-gray-400 mt-0.5">User ID: {order.userId}</p>
            </GlassTile>

            {/* Delivery Address */}
            <GlassTile className="p-4 bg-white/30">
              <div className="flex items-center gap-2 mb-2">
                <MapPin size={15} className="text-gray-500" />
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                  Delivery Address
                </p>
              </div>
              {addressParts.length > 0 ? (
                <>
                  {order.addressLabel && (
                    <p className="text-xs font-semibold text-blue-600 mb-0.5">
                      {order.addressLabel}
                    </p>
                  )}
                  <p className="text-sm text-gray-700 leading-snug">
                    {addressParts.join(", ")}
                  </p>
                </>
              ) : (
                <p className="text-sm text-gray-400 italic">No address on file</p>
              )}
            </GlassTile>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Package size={15} className="text-gray-500" />
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                Items ({order.items?.length ?? 0})
              </p>
            </div>

            <div className="space-y-3">
              {order.items?.map((item) => (
                <GlassTile key={item.orderItemId} className="p-4 bg-white/20">
                  <div className="flex justify-between items-start">

                    {/* Left: product info */}
                    <div>
                      <p className="font-medium text-gray-800">{item.productName ?? "—"}</p>
                      <p className="text-sm text-gray-600">{item.displayName}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {item.color && (
                          <span className="text-sm bg-white/40 px-3 py-1 rounded-lg text-gray-700">
                            {item.color}
                          </span>
                        )}
                        {item.size && (
                          <span className="text-sm bg-white/40 px-3 py-1 rounded-lg text-gray-700">
                            {item.size}
                          </span>
                        )}
                        {item.sku && (
                          <span className="text-sm bg-white/40 px-3 py-1 rounded-lg text-gray-500 font-mono">
                            {item.sku}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right: pricing */}
                    <div className="text-right shrink-0 ml-4">
                      <p className="text-sm text-gray-600">
                        {fmt(item.priceAtPurchase)} × {item.quantity}
                      </p>
                      <p className="font-semibold text-gray-800 mt-0.5">
                        {fmt(item.subtotal)}
                      </p>
                    </div>
                  </div>
                </GlassTile>
              ))}
            </div>

            {/* Total row */}
            <div className="flex justify-end mt-3">
              <div className="flex justify-between text-sm bg-white/40 px-4 py-2.5 rounded-xl gap-6">
                <span className="font-semibold text-gray-700">Order Total</span>
                <span className="font-bold text-gray-900">{fmt(order.totalAmount)}</span>
              </div>
            </div>
          </div>

        </div>
      )}
    </GlassTile>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   FILTER PILL
   active    → solid coloured background + white text
   inactive  → transparent, subtle hover
───────────────────────────────────────────────────────────────────────────── */
function FilterPill({ label, count, isActive, onClick, activeStyle }) {
  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-1.5 px-3 py-1 rounded-xl text-sm font-semibold
        border transition-all duration-150 select-none
        ${isActive
          ? `${activeStyle} border-transparent shadow-sm`
          : "bg-transparent border-white/30 text-gray-600 hover:bg-white/30 hover:text-gray-800"
        }
      `}
    >
      {label}
      <span
        className={`
          text-xs font-bold px-1.5 py-0.5 rounded-full leading-none
          ${isActive ? "bg-white/30" : "bg-white/50 text-gray-700"}
        `}
      >
        {count}
      </span>
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────────────────────────────────────── */

// Filter values
const FILTER_ALL       = "ALL";
const FILTER_PENDING   = "PENDING";
const FILTER_CONFIRMED = "CONFIRMED";

export default function AdminOrders() {
  const queryClient                   = useQueryClient();
  const [confirmingId, setConfirmingId] = useState(null);
  const [toast, setToast]             = useState(null);
  const [activeFilter, setActiveFilter] = useState(FILTER_ALL);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  /* ── Data fetching ── */
  const { data: orders, isLoading, isError, error } = useQuery({
    queryKey: ["admin-pending-orders"],
    queryFn: fetchPendingOrders,
  });

  /* ── Confirm mutation ── */
  const mutation = useMutation({
    mutationFn: confirmOrderReq,
    onMutate: (orderId) => setConfirmingId(orderId),
    onSuccess: (_, orderId) => {
      queryClient.setQueryData(["admin-pending-orders"], (prev) =>
        prev?.map((o) =>
          o.orderId === orderId ? { ...o, orderStatus: "CONFIRMED" } : o
        )
      );
      showToast("success", `Order #${orderId} confirmed successfully.`);
    },
    onError: (_, orderId) => {
      showToast("error", `Failed to confirm order #${orderId}. Please try again.`);
    },
    onSettled: () => setConfirmingId(null),
  });

  /* ── Counts ── */
  const total     = orders?.length ?? 0;
  const confirmed = orders?.filter((o) => o.orderStatus === "CONFIRMED").length ?? 0;
  const pending   = total - confirmed;

  /* ── Filtered list ── */
  const visibleOrders = orders?.filter((o) => {
    if (activeFilter === FILTER_CONFIRMED) return o.orderStatus === "CONFIRMED";
    if (activeFilter === FILTER_PENDING)   return o.orderStatus !== "CONFIRMED";
    return true; // ALL
  }) ?? [];

  /* ── Filter pill config ── */
  const pills = [
    {
      key:         FILTER_ALL,
      label:       "Total",
      count:       total,
      activeStyle: "bg-gray-700 text-white",
    },
    {
      key:         FILTER_PENDING,
      label:       "Pending",
      count:       pending,
      activeStyle: "bg-amber-500 text-white",
    },
    {
      key:         FILTER_CONFIRMED,
      label:       "Confirmed",
      count:       confirmed,
      activeStyle: "bg-green-600 text-white",
    },
  ];

  return (
    <div className="min-h-screen px-6 pt-24 max-w-7xl mx-auto">

      {/* ── PAGE HEADER ── */}
      <div className="flex justify-between items-center mb-10">
        <h1 className="text-3xl font-bold text-gray-800">
          Admin – Pending Orders
        </h1>

        {/* ── FILTER PILLS (right side) ── */}
        {!isLoading && !isError && (
          <div className="flex items-center gap-3 bg-white/40 px-4 py-2 rounded-2xl border border-white/30">
            <span className="text-sm font-semibold text-gray-700 mr-1">
              Orders:
            </span>
            {pills.map((pill) => (
              <FilterPill
                key={pill.key}
                label={pill.label}
                count={pill.count}
                isActive={activeFilter === pill.key}
                activeStyle={pill.activeStyle}
                onClick={() =>
                  /* clicking the already-active pill resets to ALL */
                  setActiveFilter((prev) =>
                    prev === pill.key ? FILTER_ALL : pill.key
                  )
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* ── TOAST ── */}
      {toast && (
        <div
          className={`mb-6 px-5 py-3 rounded-2xl border text-sm font-medium shadow-sm
            ${toast.type === "success"
              ? "bg-green-100/80 text-green-800 border-green-200"
              : "bg-red-100/80 text-red-800 border-red-200"
            }`}
        >
          {toast.type === "success" ? "✓ " : "✕ "}{toast.msg}
        </div>
      )}

      {/* ── LOADING ── */}
      {isLoading && (
        <GlassTile className="p-12 text-center">
          <p className="text-gray-500">Loading orders…</p>
        </GlassTile>
      )}

      {/* ── ERROR ── */}
      {isError && (
        <GlassTile className="p-12 text-center">
          <p className="text-red-500 font-semibold mb-1">Failed to load orders</p>
          <p className="text-sm text-gray-400">{error?.message}</p>
          <button
            className="mt-4 px-4 py-2 rounded-xl bg-gray-200 text-sm"
            onClick={() =>
              queryClient.invalidateQueries({ queryKey: ["admin-pending-orders"] })
            }
          >
            Retry
          </button>
        </GlassTile>
      )}

      {/* ── EMPTY STATE (after filter) ── */}
      {!isLoading && !isError && visibleOrders.length === 0 && (
        <GlassTile className="p-12 border-dashed border-2 text-center">
          {orders?.length === 0 ? (
            <>
              <p className="text-gray-500 font-medium">No pending orders found.</p>
              <p className="text-sm text-gray-400 mt-1">All caught up!</p>
            </>
          ) : (
            <>
              <p className="text-gray-500 font-medium">
                No <span className="lowercase">{activeFilter}</span> orders.
              </p>
              <button
                onClick={() => setActiveFilter(FILTER_ALL)}
                className="mt-3 text-sm text-blue-600 hover:underline"
              >
                Show all orders
              </button>
            </>
          )}
        </GlassTile>
      )}

      {/* ── ORDER LIST ── */}
      {!isLoading && !isError && visibleOrders.length > 0 && (
        <div className="space-y-6">
          {/* Active filter label */}
          {activeFilter !== FILTER_ALL && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>
                Showing{" "}
                <strong className="text-gray-700">{visibleOrders.length}</strong>{" "}
                {activeFilter.toLowerCase()} order{visibleOrders.length !== 1 ? "s" : ""}
              </span>
              <button
                onClick={() => setActiveFilter(FILTER_ALL)}
                className="text-blue-600 hover:underline ml-1"
              >
                Clear filter
              </button>
            </div>
          )}

          {visibleOrders.map((order) => (
            <OrderCard
              key={order.orderId}
              order={order}
              onConfirm={(id) => mutation.mutate(id)}
              isConfirming={confirmingId === order.orderId}
            />
          ))}
        </div>
      )}

    </div>
  );
}